<template>
  <div class="privacy-page-root">
    <div class="page-container">
      <header class="page-header">
        <h1 class="main-title">{{appName}} Privacy Disclosure</h1>
        <span class="split-line"></span>
        <h2 class="secondary-title">User Personal Data Protection Statement</h2>
      </header>

      <section class="intro-section">
        <p class="desc-text">
          When you access and operate our mobile application services, we may collect and process your personal data. This disclosure fully explains how {{appName}} gathers, uses and discloses your personal information during your use of platform services.
        </p>
        <div class="notice-card">
          <p class="notice-text">
            Kindly read this disclosure in full to fully grasp the rules we apply to safeguard your personal data and legitimate rights.
          </p>
        </div>
      </section>

      <section class="catalog-section">
        <h3 class="catalog-heading">Document Outline</h3>
        <ul class="catalog-list">
          <li>1. Collection and utilization of your personal data</li>
          <li>2. Rules for sharing, transferring and publicizing personal data</li>
          <li>3. Application specifications of Cookies and third-party SDKs</li>
          <li>4. Comprehensive security protection of personal information</li>
          <li>5. Self-management rights of your personal data</li>
          <li>6. Processing standards for minors’ personal information</li>
          <li>7. Storage regulations of user personal data</li>
          <li>8. Revision and notification mechanism of this disclosure</li>
          <li>9. Official contact channel for inquiries</li>
        </ul>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">Preface</h3>
        <p class="body-paragraph">
          {{appName}} prioritizes user data security. We maintain strict confidentiality of all collected user information and build a comprehensive data protection system to deliver stable, secure and trustworthy service experience to every user.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">1. Collection & Utilization of Personal Data</h3>
        <p class="body-paragraph">
          Personal data refers to all electronically recorded information that can identify a specific individual or reflect personal behaviors independently or in combination, including full name, birth date, biometric data, residential address, contact information, chat records, account credentials, asset information, physical health data and transaction records.
        </p>
        <p class="body-paragraph">
          Sensitive personal data refers to information that may cause risks to personal and property safety, reputational damage or unfair discrimination once leaked, illegally transmitted or misused. It covers biometric identifiers, bank account details, full chat content, asset status, location traces, health records, transaction data and personal information of users aged 14 and below.
        </p>
        <p class="emphasis-txt">You fully acknowledge and agree to the following clauses:</p>
        <ol class="ordered-list">
          <li>We develop diversified service modules to meet your personalized demands. Different functions collect different categories and ranges of personal data, which shall comply with the actual rules of each service.</li>
          <li>We continuously upgrade products and technical capabilities. When launching new or optimized functions, we may collect new types of data or adjust existing data usage modes. We will inform users via document updates, pop-up reminders or page prompts and collect data only after obtaining explicit consent. You may contact us at any time for consultation or suggestions.</li>
          <li>Data collected from a single function module can be applied to other platform services, such as customized content push and security risk alerts. If we intend to use your data beyond the authorized scope at collection, we will send separate notifications and acquire your prior consent.</li>
        </ol>
        <p class="warn-label">Special Reminder</p>
        <p class="body-paragraph">
          Our platform provides user matching and recommendation services. The nickname, age, gender, constellation, occupation, company, hometown, regular activity areas, personal signature, uploaded avatars, photos and public dynamic content you submit may be displayed and recommended to other platform users.
        </p>

        <h4 class="sub-chapter">Business Modules & Corresponding Collected Information</h4>
        <p class="body-paragraph">
          In accordance with relevant industry standards, {{appName}} is classified as a social interaction application, whose core service is online instant messaging covering text, images, voice and video. The data collected by each function are listed as follows:
        </p>
        <ol class="function-list">
          <li><span class="func-tag">Account Registration</span>: You need to provide email, nickname, gender, country, birth date and avatar to complete account creation. Meanwhile, we collect device metadata including device model, system version, unique device ID, network parameters, application list and sensor data to guarantee stable software operation.</li>
          <li><span class="func-tag">Instant Chat</span>: Basic chat services do not require extra personal data. We collect your account information to maintain service stability and account security. Sending images, voice or initiating video calls will request album and microphone permissions; refusing such access will not affect core chat functions.</li>
          <li><span class="func-tag">Video Call</span>: Camera and microphone permissions are mandatory for this function. Normal video communication cannot be realized without relevant authorization.</li>
          <li><span class="func-tag">Moments Browsing</span>: No personal data or additional system permissions are required to view public moments content.</li>
          <li><span class="func-tag">User Homepage Access</span>: Viewing others’ uploaded photos and videos needs no special permissions; initiating video calls on user homepage requires camera and microphone access.</li>
          <li><span class="func-tag">Profile Editing</span>: Album permission will be requested when replacing your avatar, and denial will not interfere with basic application usage.</li>
          <li><span class="func-tag">Payment Service</span>: Payment channels are provided by third-party cooperative payment institutions. We cannot obtain your payment-related data. Disabling relevant permissions only restricts payment functions without affecting other core services.</li>
          <li><span class="func-tag">Message Notification</span>: We apply for auto-start permission to send real-time message reminders, which you may disable in mobile system settings at any time.</li>
        </ol>
        <p class="body-paragraph">
          Camera, album and microphone permissions are disabled by default. Independent authorization pop-ups will appear when you use related functions, and you may toggle permissions freely in device settings. Disabling permissions equals revoking your data collection authorization; this will not affect data collected with your previous consent.
        </p>
        <p class="body-paragraph">
          We may send service notifications, security verification messages and user research questionnaires to your reserved email. If you publish third-party personal information on the platform, you must obtain legal authorization in advance.
        </p>
        <p class="body-paragraph">
          Your submitted information will be used during your service cycle. After account cancellation or discontinuation of specific functions, we will stop data usage and complete anonymization or permanent deletion in compliance with legal requirements.
        </p>

        <h4 class="sub-chapter">Usage Standards of Collected Data</h4>
        <p class="body-paragraph">
          We only utilize your data for the purposes specified in this disclosure. Any cross-scenario data usage requires your prior consent. When acquiring data from third parties, we will verify the legality of data sources and apply identical high-standard security protection as our native user information.
        </p>
        <p class="emphasis-txt">Exemptions of Consent Requirement</p>
        <ul class="simple-ul">
          <li>Fulfillment of statutory obligations under laws and regulations</li>
          <li>Matters related to national security and national defense</li>
          <li>Public safety, public health and general public interests</li>
          <li>Criminal investigation, litigation, trial and judgment enforcement procedures</li>
          <li>Scenarios where consent cannot be reasonably obtained to protect personal legitimate rights</li>
          <li>Information actively disclosed to the public by yourself</li>
          <li>Data collected from legally public information channels</li>
        </ul>
        <p class="body-paragraph">
          We conduct desensitization, statistical analysis and algorithm training on collected anonymous data to optimize product experience. Processed anonymous data cannot trace back to specific individuals and requires no extra user notification or consent. If we terminate platform operation, we will suspend all data collection, issue service termination announcements and erase or anonymize all stored personal records.
        </p>
        <h4 class="sub-chapter">Content Released Voluntarily by You</h4>
        <p class="body-paragraph">
          You may share texts, images and multimedia content through our platform, which may contain personal or sensitive data. Please prudently judge before publishing private information. You can delete your published content at any time, yet such content may still be stored by other users or independent third parties out of our management scope.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">2. Sharing, Transfer & Public Disclosure of Personal Data</h3>
        <h4 class="sub-chapter">Data Sharing</h4>
        <p class="body-paragraph">We will not share your personal data with external enterprises, organizations or individuals except the following scenarios:</p>
        <ul class="simple-ul">
          <li>Data sharing approved by your explicit consent</li>
          <li>Mandatory data disclosure required by laws, administrative or judicial authorities</li>
          <li>Limited necessary data sharing with affiliated brands for cross-account joint services; renewed consent will be requested for sensitive data or changed usage purposes</li>
          <li>Data provision to authorized service partners who can only access information needed for entrusted work</li>
        </ul>
        <h4 class="sub-chapter">Data Transfer</h4>
        <p class="body-paragraph">We will not transfer your personal data to any external entity unless:</p>
        <ul class="simple-ul">
          <li>You have provided explicit consent for data transfer</li>
          <li>Mergers, acquisitions or bankruptcy liquidation occur; new data holders must abide by this disclosure, otherwise renewed user authorization is required</li>
        </ul>
        <h4 class="sub-chapter">Public Disclosure</h4>
        <ul class="simple-ul">
          <li>Disclosure triggered by your active operation and explicit consent</li>
          <li>Disclosure enforced by laws and platform rules to safeguard public interests when you violate relevant agreements</li>
        </ul>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">3. Deployment of Cookies & Third-Party SDKs</h3>
        <h4 class="sub-chapter">Cookies Technology</h4>
        <p class="body-paragraph">
          We store lightweight data files named Cookies on your device to maintain stable service operation, streamline browsing experience and push personalized content. You may clear Cookies via browser settings, though partial functions relying on Cookies may become unavailable afterwards.
        </p>
        <h4 class="sub-chapter">Third-Party SDK Integration</h4>
        <p class="body-paragraph">
          Partial core functions rely on cooperative third-party SDK services, including Google Firebase Push SDK, Third-party Payment SDK, NetEase Yunxin SDK, Shengwang SDK, AppsFlyer SDK, Facebook SDK, Huawei Push SDK. Refusing corresponding permissions only disables matched third-party functions and will not interfere with other core services of {{appName}}.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">4. Security Protection of Personal Information</h3>
        <ol class="ordered-list">
          <li>We adopt encryption, desensitization and access control mechanisms to block unauthorized access, leakage, damage and loss of personal data, and arrange regular data protection training for internal staff.</li>
          <li>A full lifecycle data security management system is established. Once data leakage incidents occur, emergency response plans will be activated immediately, and users will be notified via official announcements or in-app pushes.</li>
          <li>We only collect minimal necessary data and retain user information solely for service lifecycle required by stated purposes, unless legal provisions mandate extended retention periods.</li>
          <li>The internet cannot provide absolute information security. Avoid transmitting sensitive personal data through unofficial communication channels. Contact customer service instantly if you discover account or password leakage.</li>
        </ol>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">5. Your Rights to Manage Personal Data</h3>
        <h4 class="sub-chapter">Data Inquiry</h4>
        <p class="body-paragraph">
          Within legal scope, you hold the right to access your personal data. Access entry: <strong>[Mine - Top Right Corner - Settings]</strong>. Tap your avatar in personal center to view and edit nickname, avatar, birth date and self-introduction.
        </p>
        <h4 class="sub-chapter">Data Correction</h4>
        <p class="body-paragraph">You may submit correction requests through the above entry if inaccurate personal records exist.</p>
        <h4 class="sub-chapter">Data Deletion</h4>
        <p class="body-paragraph">You can manually delete partial personal data or submit deletion requests under the following circumstances:</p>
        <ul class="simple-ul">
          <li>Our data processing violates applicable laws and regulations</li>
          <li>Data is collected and utilized without your explicit consent</li>
          <li>We materially breach the agreements recorded in this disclosure</li>
          <li>You stop using services or submit account cancellation application</li>
          <li>We permanently terminate service provision for your account</li>
        </ul>
        <h4 class="sub-chapter">Adjust Authorization Scope</h4>
        <p class="body-paragraph">You may independently enable or revoke data collection permissions, or contact customer service for authorization adjustment support.</p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">6. Processing Rules for Minors’ Information</h3>
        <p class="hard-warn-txt">This application prohibits registration and usage by users under 18 years old.</p>
        <p class="body-paragraph">
          Our services are not oriented to minors under 18. We will not actively collect or trade minor personal information. If we unintentionally obtain data from underage users, such records will be erased instantly. If relevant policies are adjusted in the future, verifiable guardian consent will be acquired before collecting any minor user data.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">7. Storage Regulations of Personal Data</h3>
        <p class="body-paragraph">
          Our service servers are deployed across multiple global regions. Your personal data may be stored and processed on overseas servers. You consent to cross-border transmission and storage of your data for all purposes outlined within this disclosure.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">8. Document Revision & Notification</h3>
        <p class="body-paragraph">
          We reserve the right to revise this privacy disclosure at our discretion. You may check the latest version via official website links at any time. Major content revisions will be announced through website notices or in-app alerts. Your continuous use of services after updates equals full acceptance of revised terms. Discontinue service access if you disagree with updated clauses.
        </p>
      </section>

      <section class="chapter-wrap">
        <h3 class="chapter-title">9. Contact Us</h3>
        <p class="body-paragraph">
          For all inquiries, feedback or disputes related to this disclosure and data protection issues, please send emails to:
          <span class="email-link">{{appName}}@{{appName}}.site</span>
        </p>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {}
  },
  created() {
    // Reserved business lifecycle hook
  }
}
</script>

<style scoped>
/* 简约高级中性灰轻奢风格 高留白 低饱和配色 */
.privacy-page-root {
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  background-color: #f9f9fb;
  padding: 48px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.page-container {
  max-width: 700px;
  margin: 0 auto;
  background: #ffffff;
  padding: 52px 44px;
  border-radius: 6px;
}
.page-header {
  text-align: center;
  margin-bottom: 48px;
}
.main-title {
  font-size: 26px;
  font-weight: 500;
  color: #1c1c24;
  margin: 0 0 10px;
  letter-spacing: 0.4px;
}
.split-line {
  display: block;
  width: 50px;
  height: 1px;
  background-color: #c8c8d0;
  margin: 0 auto 16px;
}
.secondary-title {
  font-size: 17px;
  font-weight: 400;
  color: #424252;
  margin: 0;
}
.intro-section {
  margin-bottom: 40px;
}
.desc-text {
  font-size: 14.5px;
  line-height: 1.82;
  color: #56566a;
  margin: 0 0 18px;
  text-align: justify;
}
.notice-card {
  padding: 14px 18px;
  background-color: #f4f4f8;
  border-left: 2px solid #71718c;
}
.notice-text {
  font-size: 14.5px;
  line-height: 1.75;
  color: #323248;
  margin: 0;
}
.catalog-section {
  margin-bottom: 44px;
}
.catalog-heading {
  font-size: 16px;
  font-weight: 500;
  color: #1c1c24;
  margin: 0 0 14px;
}
.catalog-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.catalog-list li {
  font-size: 14px;
  color: #56566a;
  line-height: 1.9;
  margin: 7px 0;
}
.chapter-wrap {
  margin-bottom: 46px;
}
.chapter-title {
  font-size: 17px;
  font-weight: 500;
  color: #1c1c24;
  margin: 0 0 18px;
  padding-bottom: 7px;
  border-bottom: 1px solid #eeeef3;
}
.sub-chapter {
  font-size: 15.5px;
  font-weight: 500;
  color: #323248;
  margin: 26px 0 10px;
}
.body-paragraph {
  font-size: 14.5px;
  line-height: 1.82;
  color: #56566a;
  margin: 0 0 16px;
  text-align: justify;
}
.emphasis-txt {
  font-size: 14.5px;
  font-weight: 500;
  color: #942b2b;
  margin: 14px 0 8px;
}
.warn-label {
  font-size: 14.5px;
  font-weight: 500;
  color: #942b2b;
  margin: 14px 0 4px;
}
.hard-warn-txt {
  font-size: 15.5px;
  font-weight: 500;
  color: #c02727;
  margin: 12px 0;
}
.ordered-list, .function-list {
  padding-left: 22px;
  margin: 0 0 18px;
}
.ordered-list li, .function-list li {
  font-size: 14.5px;
  line-height: 1.82;
  color: #56566a;
  margin: 9px 0;
}
.func-tag {
  font-weight: 500;
  color: #1c1c24;
}
.simple-ul {
  padding-left: 22px;
  margin: 0 0 18px;
}
.simple-ul li {
  font-size: 14.5px;
  line-height: 1.82;
  color: #56566a;
  margin: 7px 0;
}
.email-link {
  color: #2c4c80;
  font-weight: 500;
}
</style>